import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  NotificationsOff as DeclineIcon,
  NotificationsActive as UndeclineIcon,
  Send as SendIcon,
  EditCalendar as EditDateIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, addDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { INSTANT_FORMATS, calendarDaysUntil, formatCalendarDate } from '../utils/dateUtils';
import { useClinicTimezone, useFormatInstant } from '../hooks/useClinicTimezone';
import { withOfflineFallback, readByHorse, executeWrite, executeUpdate, executeDelete, useIsOffline } from '../offline';
import { useRBAC } from '../auth/useRBAC';
import { patientChartControls } from '../auth/pageControls';
import {
  DueListRow,
  ReminderLastNotice,
  reminderService,
  stageLabel,
} from '../services/reminderService';

interface RecallReminder {
  id: string;
  horseId: string;
  title: string;
  description?: string;
  dueDate: string;
  type?: string;
  notes?: string;
  status: 'PENDING' | 'COMPLETED' | 'OVERDUE' | 'DISMISSED';
  completedAt?: string;
  declinedAt?: string | null;
  createdAt: string;
}

/** Canonical types the reminder system recognizes; free-text stays legal. */
const REMINDER_TYPES = [
  'vaccination',
  'coggins',
  'exam',
  'lab',
  'dental',
  'deworming',
  'recheck',
  'other',
];

/** Display only: the stored value stays lowercase, since the reminder engine and iOS key off it. */
const reminderTypeLabel = (type: string) => type.charAt(0).toUpperCase() + type.slice(1);

/**
 * Last-send state for a reminder. Carries the STAGE as well as the outcome —
 * "notice 2 · opened" tells staff where in the sequence a client is, which the
 * old outcome-only chip dropped, along with the `failed` state entirely.
 */
const sendStateChip = (notice: ReminderLastNotice | null | undefined) => {
  if (!notice) return null;
  const where = stageLabel(notice.stage);

  if (notice.status === 'sent') {
    const bounced = notice.emailStatus === 'bounced';
    const opened = notice.emailStatus === 'opened';
    return (
      <Chip
        size="small"
        variant="outlined"
        color={bounced ? 'error' : opened ? 'success' : 'default'}
        label={`${where} · ${bounced ? 'bounced' : opened ? 'opened' : 'sent'}`}
      />
    );
  }
  if (notice.status === 'failed') {
    return (
      <Chip size="small" variant="outlined" color="error" label={`${where} · send failed`} />
    );
  }
  if (notice.status === 'skipped' && notice.skipReason === 'undeliverable') {
    return <Chip size="small" variant="outlined" color="error" label={`${where} · undeliverable`} />;
  }
  if (notice.status === 'skipped' && notice.skipReason === 'opt-out') {
    return <Chip size="small" variant="outlined" label={`${where} · client opted out`} />;
  }
  return null;
};

/** Who the next email would actually reach, per the clinic's recipient policy. */
const recipientChips = (row: DueListRow | undefined) => {
  if (!row) return null;
  if (row.recipients.length === 0) {
    return (
      <Tooltip title="No owner with an email is linked to this horse, so no reminder can be sent.">
        <Chip size="small" variant="outlined" color="warning" label="no recipients" />
      </Tooltip>
    );
  }
  return (
    <>
      {row.recipients.map(r => (
        <Chip
          key={r.clientId}
          size="small"
          variant="outlined"
          color={!r.email || !r.emailEnabled ? 'warning' : 'default'}
          label={!r.email ? `${r.name} (no email)` : !r.emailEnabled ? `${r.name} (opted out)` : r.name}
        />
      ))}
    </>
  );
};

interface Props {
  horseId: string;
}

// One count (`calendarDaysUntil` from the clinic's today, DATE-KINDS §2.9) behind the colour and
// the OVERDUE label. `isPast(new Date(dueDate))` said OVERDUE from 8 PM Eastern the day before.
const getStatusColor = (
  reminder: RecallReminder,
  daysLeft: number | null,
): 'error' | 'warning' | 'success' | 'default' => {
  if (reminder.status === 'COMPLETED') return 'success';
  if (reminder.status === 'DISMISSED') return 'default';
  if (reminder.status === 'OVERDUE' || (daysLeft !== null && daysLeft < 0)) return 'error';
  return daysLeft !== null && daysLeft <= 14 ? 'warning' : 'default';
};

const RecallRemindersPanel: React.FC<Props> = ({ horseId }) => {
  const rbac = useRBAC();
  // Add / edit-due-date / Complete / Dismiss / Decline / Send / Delete all hit
  // recall-reminders.controller.ts: create :33, update :150, sendNow :136, remove :173 — one
  // slug, manage:medical_records. (The four SETTINGS routes on that controller are
  // @Roles(OWNER, ADMIN) with no permission at all, but none of them is reachable from here —
  // they live on the clinic settings screen.)
  const can = patientChartControls(rbac);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    dueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    type: '',
    notes: '',
  });

  const isOffline = useIsOffline();
  // `completedAt` is an INSTANT and renders in the CLINIC's zone; `dueDate` is a
  // CALENDAR day and must never be re-zoned at all. See the render site below.
  const formatInstant = useFormatInstant();
  const clinicZone = useClinicTimezone();
  const [editRow, setEditRow] = useState<RecallReminder | null>(null);
  const [editDate, setEditDate] = useState('');

  const { data: reminders = [], isLoading, isError, refetch } = useQuery<RecallReminder[]>(
    ['recall-reminders', horseId],
    withOfflineFallback(
      () => api.get('/recall-reminders', { params: { horseId } }).then(r => r.data),
      () => readByHorse('recallReminders', horseId) as Promise<RecallReminder[]>,
    ),
    { enabled: !!horseId },
  );

  // Send state + resolved recipients. Server-only data (the notice ledger never
  // reaches the offline replica), so fetch online only — the actionable list
  // above stays offline-capable. Scoping to one horse also lifts the clinic-wide
  // -365..+60d window server-side, which otherwise hides an annual vaccine or
  // Coggins due further out than 60 days.
  const { data: dueListRows = [] } = useQuery<DueListRow[]>(
    ['recall-reminders', 'send-state', horseId],
    () => reminderService.getDueList({ horseId }),
    { enabled: !!horseId && !isOffline },
  );
  const lastNoticeById = useMemo(
    () => new Map(dueListRows.map(r => [r.id, r.lastNotice])),
    [dueListRows],
  );
  const rowById = useMemo(() => new Map(dueListRows.map(r => [r.id, r])), [dueListRows]);

  const createMutation = useMutation(
    (payload: any) =>
      executeWrite({
        entityType: 'recallReminder',
        table: 'recallReminders',
        payload,
        networkFn: () => api.post('/recall-reminders', payload).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['recall-reminders', horseId]);
        enqueueSnackbar('Reminder created', { variant: 'success' });
        setDialogOpen(false);
        setForm({
          title: '',
          description: '',
          dueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
          type: '',
          notes: '',
        });
      },
      onError: () => { enqueueSnackbar('Failed to create reminder', { variant: 'error' }); },
    },
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      executeUpdate({
        entityType: 'recallReminder',
        table: 'recallReminders',
        entityId: id,
        payload: data,
        networkFn: () => api.put(`/recall-reminders/${id}`, data).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['recall-reminders', horseId]);
        queryClient.invalidateQueries(['recall-reminders', 'send-state', horseId]);
        enqueueSnackbar('Reminder updated', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to update reminder', { variant: 'error' }); },
    },
  );

  /**
   * Send now. Deliberately a PLAIN network call, never executeWrite: a queued
   * email replayed on reconnect would defeat claim-before-send (INV-52) and
   * double-email a client. Offline the button is disabled with a reason.
   */
  const sendNowMutation = useMutation((id: string) => reminderService.sendNow(id), {
    onSuccess: ({ sent, skipped }) => {
      queryClient.invalidateQueries(['recall-reminders', 'send-state', horseId]);
      if (sent === 0) {
        enqueueSnackbar(
          skipped > 0
            ? 'Nothing sent — every recipient was opted out, undeliverable, or already emailed today.'
            : 'Nothing sent — this horse has no owner with an email.',
          { variant: 'warning' },
        );
        return;
      }
      enqueueSnackbar(
        `Reminder emailed to ${sent} recipient${sent === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped)` : ''}`,
        { variant: 'success' },
      );
    },
    onError: (err: any) => {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to send reminder', {
        variant: 'error',
      });
    },
  });

  const deleteMutation = useMutation(
    (id: string) =>
      executeDelete({
        entityType: 'recallReminder',
        table: 'recallReminders',
        entityId: id,
        networkFn: () => api.delete(`/recall-reminders/${id}`).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['recall-reminders', horseId]);
        queryClient.invalidateQueries(['recall-reminders', 'send-state', horseId]);
        enqueueSnackbar('Reminder deleted', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to delete reminder', { variant: 'error' }); },
    },
  );

  const handleSubmit = () => {
    if (!form.title.trim() || !form.dueDate) {
      enqueueSnackbar('Title and due date are required', { variant: 'warning' });
      return;
    }
    createMutation.mutate({
      horseId,
      title: form.title,
      description: form.description || undefined,
      // WRITE ANCHOR — deliberately UTC MIDNIGHT, and the one calendar column that must
      // NOT move to `ymdToNoonUtcIso`. `form.dueDate` is a native `type="date"` string, so
      // `new Date('2026-01-15')` is UTC midnight by spec and the picked DAY survives
      // exactly (this is not the W10 MUI-DatePicker trap, which really can store the wrong
      // day). The reminder SCHEDULER is built on that anchor: reminder-scheduler.service.ts
      // computes `today` as UTC midnight and does day arithmetic off `dueDate` directly
      // (`stageApplies`, `dueDate: { lt: today }`), documenting "dueDate values are
      // date-picker dates (midnight UTC)". Re-anchoring only this client to noon would put
      // half a day inside every stage-window comparison and fire outreach a day late for
      // the rows it wrote. Collapsing the column needs the scheduler changed and the
      // existing rows backfilled, in one coordinated change — not here.
      dueDate: new Date(form.dueDate).toISOString(),
      type: form.type || undefined,
      notes: form.notes || undefined,
    });
  };

  const pending = reminders.filter(r => r.status === 'PENDING' || r.status === 'OVERDUE');
  const completed = reminders.filter(r => r.status === 'COMPLETED' || r.status === 'DISMISSED');
  const displayed = showCompleted ? reminders : pending;

  if (isLoading) {
    return <Typography color="textSecondary">Loading reminders...</Typography>;
  }

  // Fail loud: rendering the empty state on a failed fetch would tell staff
  // "nothing is due" when the truth is "we don't know".
  if (isError && !isOffline) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="error" gutterBottom>
          Couldn't load reminders for this horse — this list may be incomplete.
        </Typography>
        <Button size="small" variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="subtitle2" color="textSecondary">
            {pending.length} pending · {completed.length} completed
          </Typography>
          {completed.length > 0 && (
            <Button size="small" onClick={() => setShowCompleted(!showCompleted)}>
              {showCompleted ? 'Hide completed' : 'Show completed'}
            </Button>
          )}
        </Box>
        {can.recallWrite && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Reminder
          </Button>
        )}
      </Box>

      {displayed.length === 0 ? (
        <Box py={4} textAlign="center">
          <Typography color="textSecondary">
            {showCompleted ? 'No reminders' : 'No pending reminders'}
          </Typography>
        </Box>
      ) : (
        <Box>
          {displayed.map(reminder => {
            const daysLeft = calendarDaysUntil(reminder.dueDate, clinicZone);
            const statusChipColor = getStatusColor(reminder, daysLeft);
            const isOverdue = reminder.status === 'PENDING' && daysLeft !== null && daysLeft < 0;
            const displayStatus = isOverdue ? 'OVERDUE' : reminder.status;
            return (
              <Paper key={reminder.id} variant="outlined" sx={{ mb: 1.5, p: 2 }}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                      <Typography variant="subtitle2">{reminder.title}</Typography>
                      <Chip label={displayStatus} color={statusChipColor} size="small" />
                      {reminder.type && (
                        <Chip label={reminderTypeLabel(reminder.type)} variant="outlined" size="small" />
                      )}
                      {reminder.declinedAt && (
                        <Chip label="outreach declined" size="small" variant="outlined" />
                      )}
                      {!isOffline && sendStateChip(lastNoticeById.get(reminder.id))}
                    </Box>
                    {!isOffline &&
                      !reminder.declinedAt &&
                      (reminder.status === 'PENDING' || reminder.status === 'OVERDUE') && (
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                          <Typography variant="caption" color="textSecondary">
                            Emails:
                          </Typography>
                          {recipientChips(rowById.get(reminder.id))}
                        </Box>
                      )}
                    {reminder.description && (
                      <Typography variant="body2" color="textSecondary" mb={0.5}>
                        {reminder.description}
                      </Typography>
                    )}
                    {/* TWO KINDS ON ONE LINE — DATE-KINDS.md §1.1 (dueDate) and §1.3
                        (completedAt); burndown rows §2.2 and §2.3.

                        · dueDate     CALENDAR. `recall-reminders.controller.ts:41,158` stores
                                      `new Date(body.dueDate)` over the `yyyy-MM-dd` this panel
                                      posts, i.e. UTC MIDNIGHT — and this file already knew it
                                      (the edit handler below slices the ISO string rather than
                                      formatting it, "because dueDate is midnight UTC"). The
                                      render did not: `format(new Date(iso))` re-read that
                                      instant in the VIEWER'S zone, so a reminder due Dec 31
                                      printed Dec 30 for every US clinic — the day a vet chases
                                      a Coggins test on. Parsing the date PART removes the
                                      re-zone entirely, so the string is identical in Niue and
                                      Kiritimati alike (e2e/specs/chart-panels.spec.ts).
                        · completedAt INSTANT. `recall-reminders.service.ts` stamps it with
                                      `new Date()` on the COMPLETE transition (§1.3, "Messaging"
                                      group) — a real moment, so it is SUPPOSED to name a zone.
                                      The clinic's, not the viewer's.

                        ⚠ Do not "simplify" these to one helper. Routing dueDate through the
                        instant formatter re-introduces the rollback; date-parting completedAt
                        moves a 9 PM completion into the next day. Opposite fixes — the INVERSE
                        trap in .claude/rules/data-model-landmines.md. */}
                    <Typography
                      variant="caption"
                      color={isOverdue ? 'error' : 'textSecondary'}
                      data-testid={`recall-dates-${reminder.id}`}
                    >
                      Due: {formatCalendarDate(reminder.dueDate, 'MMM d, yyyy')}
                      {reminder.completedAt &&
                        ` · Completed: ${formatInstant(reminder.completedAt, INSTANT_FORMATS.date)}`}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    {can.recallWrite && (reminder.status === 'PENDING' || reminder.status === 'OVERDUE') && (
                      <>
                        {!reminder.declinedAt && (
                          // A disabled MUI button swallows its tooltip, so the
                          // reason has to hang on a wrapping span.
                          <Tooltip
                            title={
                              isOffline
                                ? 'Sending needs a connection'
                                : 'Email this reminder to the owners now'
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<SendIcon />}
                                disabled={isOffline || sendNowMutation.isLoading}
                                onClick={() => sendNowMutation.mutate(reminder.id)}
                              >
                                Send
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          title="Change the due date"
                          onClick={() => {
                            setEditRow(reminder);
                            // Slice the stored ISO string rather than format()
                            // it: dueDate is midnight UTC, so local formatting
                            // renders the PREVIOUS day west of Greenwich and
                            // saving it back would walk the date backwards.
                            setEditDate(reminder.dueDate.slice(0, 10));
                          }}
                        >
                          <EditDateIcon fontSize="small" />
                        </IconButton>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() =>
                            updateMutation.mutate({ id: reminder.id, data: { status: 'COMPLETED' } })
                          }
                        >
                          Complete
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<CancelIcon />}
                          onClick={() =>
                            updateMutation.mutate({ id: reminder.id, data: { status: 'DISMISSED' } })
                          }
                        >
                          Dismiss
                        </Button>
                        <IconButton
                          size="small"
                          title={
                            reminder.declinedAt
                              ? 'Resume client emails for this item'
                              : 'Decline — keep on the list, never email the client'
                          }
                          onClick={() =>
                            updateMutation.mutate({
                              id: reminder.id,
                              data: {
                                declinedAt: reminder.declinedAt ? null : new Date().toISOString(),
                              },
                            })
                          }
                        >
                          {reminder.declinedAt ? (
                            <UndeclineIcon fontSize="small" />
                          ) : (
                            <DeclineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </>
                    )}
                    {can.recallWrite && (
                      <IconButton size="small" onClick={() => deleteMutation.mutate(reminder.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Recall Reminder</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              fullWidth
              placeholder="e.g. Annual Vaccines, Dental Float"
            />
            <TextField
              label="Due Date *"
              type="date"
              value={form.dueDate}
              onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Type"
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              fullWidth
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              helperText="Vaccination-type reminders auto-complete when the vaccine is recorded"
            >
              <option value="">—</option>
              {REMINDER_TYPES.map(t => (
                <option key={t} value={t}>
                  {reminderTypeLabel(t)}
                </option>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={createMutation.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Change due date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {editRow?.title}
          </Typography>
          <TextField
            label="Due date"
            type="date"
            fullWidth
            value={editDate}
            onChange={e => setEditDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!editDate || updateMutation.isLoading}
            onClick={() => {
              if (!editRow || !editDate) return;
              // Same ISO form the create path uses, so a reminder never lands a
              // day off depending on which control edited it. Goes through
              // updateMutation, so an offline edit queues like every other write.
              updateMutation.mutate({
                id: editRow.id,
                data: { dueDate: new Date(editDate).toISOString() },
              });
              setEditRow(null);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecallRemindersPanel;
